import type { Navigator, Screen } from "../app/screen";
import { Api } from "../net/api";
import { pageShell, note } from "../ui/page";
import { allCards, allHeroes, loadCardNames, cardName, cardRealm, cardRecord } from "../match/cards";
// ⭐ Where each card can be won, generated from the campaign data - tools/gen_web_card_sources.py.
import { CARD_SOURCES, RANDOM_CARD_SOURCE } from "../card-sources.generated";
import { REALM_COLOUR } from "../card-frames.generated";
import { cardEl } from "../match/card";
import type { RuntimeObject } from "../match/state";
import {
  DEFAULT_LIMITS, canInclude, deckProblems, effectiveCost, loyaltyOf,
  type DeckLimits,
} from "../match/deckRules";
import { audio } from "../fx/audio";

/**
 * THE DECK BUILDER.
 *
 * ⭐ Alex, 2026-08-14: *"there is also a build a deck feature that is extremely important... Building
 * a deck is a very important and integral part of the UX so the UI and the build a deck experience
 * must be high end and you must use your design skills, and game skills to make it a wonder and
 * beautiful user experience."*
 *
 * ⭐ **THE ONE IDEA THIS SCREEN IS BUILT AROUND: YOUR HERO RE-PRICES YOUR WHOLE COLLECTION.**
 * `HeroDeckbuildingRules` scores every card's realm against your hero's - Alliance, Tolerated,
 * Hostile, Nemesis - and that changes what the card COSTS (+0/+1/+2) or forbids it outright. So the
 * hero is not a portrait at the top of a list: **it is the lens the entire collection is seen
 * through.** Changing it here re-prices and re-locks every card in place, live. That is the moment
 * this screen exists for, and it is why the hero sits at the top with the collection beneath it
 * rather than in a settings row.
 *
 * ⭐ **THE THREE THINGS A DECK BUILDER MUST ANSWER AT A GLANCE**, and where each is:
 *   1. *How far am I?*      the count rail, `30/35`, always in the same place
 *   2. *Is my curve sane?*  the histogram, above the deck list, in EFFECTIVE cost
 *   3. *What is stopping me saving?*  every problem at once, never one at a time
 *
 * ⚠️ **THE CURVE USES EFFECTIVE COST, NOT PRINTED COST.** A curve drawn from printed costs would be
 * a lie in exactly the decks that need it most - the off-realm ones, where every card is +1 or +2.
 *
 * ⚠️ **`ownedCardIds` ARE OWNED-CARD ENTITIES, NOT CARD IDS.** Two copies of one card are two
 * different owned entities, so adding a copy means finding an unused entity of that card.
 */

interface OwnedCard {
  id: string;
  cardId: string;
  isOwned?: boolean;
  isNft?: boolean;
}

interface DeckDto {
  id?: string;
  name?: string;
  heroId?: string;
  ownedCardIds?: string[];
  /**
   * How many decks of their OWN this player may hold: one free, the rest bought.
   *
   * ⚠️ Rides on the deck list so the builder never has to ask twice and get two answers.
   */
  deckSlots?: number;
  /**
   * One of the four decks every account is seeded with.
   *
   * ⭐ **COMPUTED BY THE SERVER FROM THE CAPTURE'S IDS, NEVER STORED.** So it cannot drift, needs no
   * migration, and cannot be defeated by renaming a deck "Starter Deck - ...".
   */
  isStarter?: boolean;
}

export const DecksScreen: Screen = {
  id: "decks",

  /**
   * 🔴 **THIS SAID `["SAVE", "BACK"]` AND LOGGED A MISSING AFFORDANCE ON EVERY LIVE VISIT.** Four of
   * a player's five decks are read-only starters, and a starter deck deliberately renders COPY TO A
   * NEW DECK instead of SAVE, so the DEFAULT view of this screen failed its own contract. The
   * requirement was always "a way to commit your work", not one particular button.
   */
  mustOffer: [["SAVE", "COPY TO A NEW DECK"], "BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "YOUR DECKS", "build what you bring");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "deck";
    shell.body.append(root);
    root.append(note("Loading your collection..."));

    await loadCardNames();

    let limits: DeckLimits = DEFAULT_LIMITS;
    try {
      const cfg = await Api.gameConfig() as Record<string, number>;
      // ⚠️ Each value is taken only if the server actually sent a number. A missing key must not
      // become `undefined` and then `NaN` in a comparison, which silently permits any deck size.
      limits = {
        minCardsInDeck: num(cfg["minCardsInDeck"], DEFAULT_LIMITS.minCardsInDeck),
        maxCardsInDeck: num(cfg["maxCardsInDeck"], DEFAULT_LIMITS.maxCardsInDeck),
        maxDeckCount: num(cfg["maxDeckCount"], DEFAULT_LIMITS.maxDeckCount),
      };
    } catch {
      // Keep the defaults - they match the live config and are not more permissive than it.
    }

    let owned: OwnedCard[] = [];
    let decks: DeckDto[] = [];
    /** Card ids that came from an AWARD receipt, i.e. genuinely won. Not the starter or beta grant. */
    const wonIds = new Set<string>();
    try {
      // ⚠️ **DECKS COME FROM `Deck`, NOT FROM `UserData`.** The two disagreed: this screen read
      // `UserData.decks` while the PLAY screen read `GET /api/v1/Deck`, which used to serve a static
      // capture of the four starter decks - so a deck you built appeared here and was unplayable
      // there. One owner, and it is the route that also marks `isStarter`.
      /*
       * ⭐ THE AWARD LEDGER IS LOADED TOO, AND IT ANSWERS A QUESTION THE COLLECTION CANNOT.
       *
       * 🔴 Alex: *"anything i win should be mintable no? but it says i own these and i am only able
       * to mint one card."* Both halves are true and that is exactly the problem — he OWNS 108 cards
       * and has WON one. The other 107 are the 59-card starter collection and the 48-card beta
       * grant, which he himself ruled unmintable: *"the free starter packs are not to be minted.
       * Only the cards won for completing the campaigns."*
       *
       * ⛔ The collection had NO WAY TO TELL THEM APART. Every card said "owned", so the Tent
       * offering exactly one looked like a bug rather than the rule working. `Player__Awards` is
       * the receipt — the only record of what was EARNED — so the builder now reads it and marks
       * those cards. Failure is swallowed: a missing ledger must degrade to "no badges", never to a
       * collection that will not load.
       */
      const [ud, list, aw] = await Promise.all([
        Api.userData() as Promise<{ ownedCards?: OwnedCard[] }>,
        Api.decks() as Promise<DeckDto[]>,
        Api.awards().catch(() => ({ awards: [] as Array<{ cardIds: string[] }> })),
      ]);
      owned = Array.isArray(ud?.ownedCards) ? ud.ownedCards.filter((c) => c?.isOwned !== false) : [];
      decks = Array.isArray(list) ? list : [];
      for (const a of aw?.awards ?? []) for (const id of a?.cardIds ?? []) wonIds.add(String(id));
    } catch (e) {
      root.replaceChildren(note(`Could not load your collection: ${describe(e)}`, true));
      return;
    }

    if (!decks.length) {
      root.replaceChildren(note("You have no decks yet.", true));
      return;
    }

    // ── state ───────────────────────────────────────────────────────────────────────────────────
    /**
     * How the collection is ordered, and whether it is filtered to minted cards.
     *
     * ⭐ Alex, 2026-08-14: *"there will be NFT cards that users will have that they can pull from to
     * build their decks. So the build page needs to show all the cards, there should be a sort
     * function, to make it easy to use and it should show their NFTs if they have them."*
     *
     * ⚠️ **SORTING IS A VIEW, NOT A RULE.** It never changes what is legal - only the order you
     * meet it in - so it lives here rather than anywhere near `deckRules`.
     */
    type SortKey = "cost" | "name" | "realm";
    let sortBy: SortKey = "cost";
    let nftOnly = false;
    /**
     * Show only cards a campaign stage awards BY NAME.
     *
     * 🔴 THE LABEL WAS "WINNABLE" AND IT SAID SOMETHING FALSE. Alex: *"it still shows just 5
     * winnable NFTs."* All 171 are winnable — `ProgressionStore.CardPool()` takes every card id in
     * the database with NO filter, and ~88 of a full playthrough's 93 grants are random draws from
     * it. Five is the number of cards a stage NAMES, which is a completely different fact.
     *
     * The filter is genuinely useful and kept; the LABEL is what was lying, so it now says
     * `NAMED` — see the button.
     */
    let namedOnly = false;
    /**
     * Hide the cards you do not own.
     *
     * ⭐ Alex, 2026-08-14: *"why so few cards to choose from? shouldn't the player be able to choose
     * from all the 121 cards, plus any NFTs that they have?"* Two different limits were being read
     * as one. The builder now lists the WHOLE set (171) and marks what is not yours - because a
     * card you cannot see is a card you cannot want, and cards are going to be minted.
     *
     * ⚠️ **OWNERSHIP IS THE ENGINE'S RULE, NOT A UI CHOICE.** `GameConfig.bypassCardOwnership` is
     * false, so an unowned card is unusable no matter how it is displayed. Showing it is an
     * invitation, never a promise.
     */
    let ownedOnly = false;

    /**
     * Narrow the collection by name.
     *
     * ⭐ **171 CARDS IS PAST THE POINT WHERE SCROLLING IS A WAY TO FIND ONE.** On a landscape phone
     * the collection is four columns of a 43-row grid; on desktop it is 22 rows. Sort orders them,
     * the three filters cut the set by a property, and neither answers "where is Ambush Wolf".
     *
     * 🔴 **IT DOES NOT GO THROUGH `draw()`, DELIBERATELY.** A redraw rebuilds all 171 card
     * elements - real `cardEl` objects with art, frame and badges - and doing that per keystroke on
     * a phone is a stutter, and it takes the caret out of the field every time. The search instead
     * toggles a class on slots that already exist, so it is O(171) attribute writes and the field
     * keeps focus. `applySearch()` is re-run at the end of every `draw()` so a sort or a filter
     * never resurrects a card the search has excluded.
     */
    let search = "";

    let deck: DeckDto = decks[0]!;
    let chosen = new Set<string>(deck.ownedCardIds ?? []);

    // Owned entities grouped by the card they are, so "add a copy" can find an unused one.
    const byCard = new Map<string, OwnedCard[]>();
    for (const o of owned) {
      if (!o?.cardId) continue;
      const list = byCard.get(o.cardId) ?? [];
      list.push(o);
      byCard.set(o.cardId, list);
    }

    const heroRealm = () => cardRealm(deck.heroId);

    /**
     * Does the player hold a MINTED copy of this card?
     *
     * ⚠️ Asked of the OWNED ENTITIES, not of the card: the same card can be held as an ordinary
     * grant and as an NFT, and it is the entity that is minted.
     */
    function hasNft(cardId: string): boolean {
      return (byCard.get(cardId) ?? []).some((o) => o.isNft === true);
    }

    function copiesOf(cardId: string): number {
      return (byCard.get(cardId) ?? []).filter((o) => chosen.has(o.id)).length;
    }

    function addCopy(cardId: string): void {
      const free = (byCard.get(cardId) ?? []).find((o) => !chosen.has(o.id));
      if (!free) return;
      chosen.add(free.id);
      audio.play("cardDrop", 0.7);
      draw();
    }

    function removeCopy(cardId: string): void {
      const used = (byCard.get(cardId) ?? []).filter((o) => chosen.has(o.id));
      const last = used[used.length - 1];
      if (!last) return;
      chosen.delete(last.id);
      audio.play("cardPick", 0.6);
      draw();
    }

    // ── the screen ──────────────────────────────────────────────────────────────────────────────
    const head = document.createElement("div");
    head.className = "deck__head";

    const panes = document.createElement("div");
    panes.className = "deck__panes";

    const collection = document.createElement("section");
    collection.className = "deck__collection";

    /**
     * 🔴 **THE SORT AND FILTER CONTROLS WERE 0% ON SCREEN ON A LANDSCAPE PHONE, AND THEY ARE THE
     * ONLY THING THAT MAKES 171 CARDS TRACTABLE THERE.** Measured live on iPhone 14 landscape:
     * `.deck__view` sat at left -246, top 596, in a viewport 664x390. It is a `display:flex` row
     * 1161px wide, and on a short screen `.deck__head` becomes a 154px-wide COLUMN - so the row
     * ran off both edges of a container seven times narrower than itself.
     *
     * ⭐ **THE FIX IS OWNERSHIP, NOT A WIDTH.** These controls order and filter the COLLECTION, so
     * they belong to the collection's pane, not to the head. That is also what the desktop layout
     * had always been faking: the view row was a second row of `.deck__head`'s grid that happened
     * to land above the collection. Making it real fixes the phone AND removes the head grid's
     * fifth child from a four-column template.
     */
    const view = document.createElement("div");
    view.className = "deck__view";

    /**
     * The sort and filter groups, on a screen with no room for them in a row.
     *
     * ⚠️ **MEASURED BEFORE IT WAS DESIGNED.** Laid out above the collection on an iPhone 14
     * landscape the bar is 1,360px of content in a 340px column: it wraps to FIVE rows, 359px of a
     * 259px body, and the collection is left 27px tall. So "just let it wrap" is not an option that
     * exists, and neither is `overflow-x` (a control you have to scroll sideways to discover is the
     * same defect as one off the edge).
     *
     * ⭐ **THE SHEET HOLDS THE SAME NODES, NOT COPIES.** `draw()` builds the chips once into
     * `.deck__viewgroups` and then appends that ONE element to whichever container is open. A
     * phone-only duplicate set of filter buttons would be two owners of the same control, and they
     * drift the first time one is changed.
     */
    const viewSheet = document.createElement("div");
    viewSheet.className = "settings deck__viewsheet";
    viewSheet.setAttribute("role", "dialog");
    viewSheet.setAttribute("aria-label", "Sort and filter the collection");
    viewSheet.hidden = true;
    let sheetOpen = false;

    const side = document.createElement("aside");
    collection.setAttribute("aria-label", "Your collection");
    side.className = "deck__side";

    const foot = document.createElement("div");
    foot.className = "deck__foot";

    panes.append(view, collection, side);
    root.replaceChildren(head, panes, foot, viewSheet);

    /**
     * Choose a hero, name the deck, create it.
     *
     * ⚠️ **AN OVERLAY, NOT A ROUTE.** Creating a deck is a step inside building one; sending the
     * player to another screen and back loses the thread and the scroll position.
     */
    function openHeroPicker(): void {
      const scrim = document.createElement("div");
      scrim.className = "settings";           // the same overlay language as the sound panel
      scrim.setAttribute("role", "dialog");
      scrim.setAttribute("aria-label", "New deck");

      const card = document.createElement("div");
      card.className = "settings__card newdeck";

      const h = document.createElement("h2");
      h.className = "settings__title";
      h.textContent = "CHOOSE YOUR HERO";

      const why = document.createElement("p");
      why.className = "settings__status";
      why.textContent = "Your hero decides what every card costs you, and which realm you cannot "
        + "take at all.";

      const grid = document.createElement("div");
      grid.className = "newdeck__heroes";

      let chosenHero: string | null = null;

      const name = document.createElement("input");
      name.className = "duel__codein newdeck__name";
      name.placeholder = "DECK NAME";
      name.maxLength = 40;
      name.setAttribute("aria-label", "Deck name");

      const create = document.createElement("button");
      create.type = "button";
      create.className = "plate";
      create.dataset["label"] = "CREATE";
      const cf = document.createElement("span");
      cf.className = "plate__face";
      cf.setAttribute("aria-hidden", "true");
      const cl = document.createElement("span");
      cl.className = "plate__label";
      cl.textContent = "CREATE";
      create.append(cf, cl);
      create.disabled = true;
      create.classList.add("plate--disabled");

      const issues = document.createElement("p");
      issues.className = "settings__status";

      for (const hero of allHeroes()) {
        if (!hero.id) continue;
        const realm = hero.quadrant;
        const b = document.createElement("button");
        b.type = "button";
        b.className = "newdeck__hero";
        b.dataset["label"] = String(hero.title ?? hero.id);
        if (realm && REALM_COLOUR[realm]) b.style.setProperty("--realm", REALM_COLOUR[realm]!);

        const art = document.createElement("span");
        art.className = "newdeck__heroart";
        if (hero.artUrl) art.style.backgroundImage = `url("/art/cards/${hero.artUrl}.webp")`;

        const nm = document.createElement("span");
        nm.className = "newdeck__heroname";
        nm.textContent = String(hero.title ?? "");

        const rl = document.createElement("span");
        rl.className = "newdeck__herorealm";
        rl.textContent = realm ? realm.replace(/_/g, " ") : "";

        const pw = document.createElement("span");
        pw.className = "newdeck__heropower";
        pw.textContent = hero.heroPowerName ? `${hero.heroPowerName} · ${hero.heroPowerCost ?? 0}` : "";
        b.title = String(hero.heroPowerDescription ?? "");

        b.append(art, nm, rl, pw);
        b.addEventListener("click", () => {
          chosenHero = String(hero.id);
          for (const other of Array.from(grid.children)) other.classList.remove("newdeck__hero--on");
          b.classList.add("newdeck__hero--on");
          // A sensible default the player can overwrite - never a name we keep silently.
          if (!name.value.trim()) name.value = `${hero.title ?? "New"} deck`;
          create.disabled = false;
          create.classList.remove("plate--disabled");
          audio.play("cardHover", 0.5);
        });
        grid.append(b);
      }

      create.addEventListener("click", async () => {
        if (!chosenHero) return;
        create.disabled = true;
        try {
          const made = await Api.createDeck({
            name: name.value.trim() || "New deck",
            heroId: chosenHero,
            // ⚠️ Deliberately EMPTY. A new deck starts illegal (0 of 30) and the builder says so -
            // pre-filling it with cards would be inventing a deck the player did not choose.
            ownedCardIds: [],
          }) as DeckDto;
          decks.push(made);
          deck = made;
          chosenHero = null;
          chosen = new Set();
          scrim.remove();
          audio.play("click");
          draw();
        } catch (e) {
          // The server refuses past the deck limit WITH a message. Show it.
          issues.textContent = describe(e);
          issues.classList.add("settings__status--error");
          create.disabled = false;
        }
      });

      const close = document.createElement("button");
      close.type = "button";
      close.className = "plate plate--quiet";
      close.dataset["label"] = "CANCEL";
      const xf = document.createElement("span");
      xf.className = "plate__face";
      xf.setAttribute("aria-hidden", "true");
      const xl = document.createElement("span");
      xl.className = "plate__label";
      xl.textContent = "CANCEL";
      close.append(xf, xl);
      close.addEventListener("click", () => scrim.remove());

      const row = document.createElement("div");
      row.className = "newdeck__foot";
      row.append(name, close, create);

      card.append(h, why, grid, issues, row);
      scrim.append(card);
      scrim.addEventListener("click", (e) => { if (e.target === scrim) scrim.remove(); });
      document.body.append(scrim);
    }

    /**
     * Hide the slots whose card name does not contain the search text.
     *
     * ⚠️ **A CLASS, NOT `display:none` SET INLINE, AND NOT A REBUILD.** The slots stay in the DOM
     * with their art already decoded, so clearing the field is instant and typing does not restart
     * 171 image loads.
     *
     * 🔴 **AN EMPTY RESULT MUST SAY WHY IT COULD BE EMPTY.** Three filters can be on at once, and a
     * blank grid under a search box reads as "we do not have that card" when the truth is often
     * "SHOW ONLY is hiding it". A silent empty state is the same defect class as a screen with no
     * way out: correct, and unreadable.
     */
    function applySearch(): void {
      const q = search;
      let shown = 0;
      for (const el of Array.from(collection.querySelectorAll<HTMLElement>(".deck__slot"))) {
        const hit = q === "" || (el.dataset["find"] ?? "").includes(q);
        el.classList.toggle("deck__slot--hidden", !hit);
        if (hit) shown += 1;
      }
      let note = collection.querySelector<HTMLElement>(".deck__empty");
      if (q !== "" && shown === 0) {
        if (!note) {
          note = document.createElement("p");
          note.className = "deck__empty";
          collection.append(note);
        }
        note.textContent = (ownedOnly || namedOnly || nftOnly)
          ? `No card here matching "${search}", and SHOW ONLY is hiding part of the set. `
            + `Turn a filter off to search all of it.`
          : `No card matching "${search}".`;
      } else {
        note?.remove();
      }
    }

    function draw(): void {
      const hr = heroRealm();
      // ⭐ **THE PRE-BUILT DECKS SHOW NO EDIT CONTROLS AT ALL.** Alex, 2026-08-14: *"the edit or
      // delete function should not even show for the free pre-built decks."* Right - a disabled
      // button still invites the click and then refuses it, which teaches nothing and reads as
      // broken. Absent is honest; the server refuses too, so this is presentation, not the rule.
      const readOnly = deck.isStarter === true;
      // ⭐ The page takes the realm's colour - see `.deck[data-realm]`. It answers "which realm is
      // this deck" before a single card is read, and it changes the moment you switch decks.
      if (hr) {
        root.dataset["realm"] = hr;
        if (REALM_COLOUR[hr]) root.style.setProperty("--realm", REALM_COLOUR[hr]!);
      } else {
        delete root.dataset["realm"];
        root.style.removeProperty("--realm");
      }
      const entries = [...byCard.keys()]
        .map((cardId) => ({
          cardId,
          copies: copiesOf(cardId),
          limitInDeck: num(cardRecord(cardId)?.limitInDeck, 1),
          realm: cardRealm(cardId),
        }))
        .filter((e) => e.copies > 0);
      const total = entries.reduce((n, e) => n + e.copies, 0);
      const problems = deckProblems(entries, hr, limits);

      // ── head: which deck, which hero, how far ────────────────────────────────────────────────
      head.replaceChildren();

      const picker = document.createElement("div");
      picker.className = "deck__picker";
      for (const d of decks) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "deck__tab" + (d.id === deck.id ? " deck__tab--on" : "");
        b.dataset["label"] = String(d.name ?? "Deck");
        b.textContent = String(d.name ?? "Deck");
        b.addEventListener("click", () => {
          deck = d;
          chosen = new Set(d.ownedCardIds ?? []);
          audio.play("click", 0.6);
          draw();
        });
        picker.append(b);
      }

      // ⭐ **BUILDING A DECK STARTS WITH THE HERO.** Alex, 2026-08-14: *"we need a page to build a
      // deck, these pages are more choose a deck."* He is right, and the missing half was CREATION.
      // The hero comes first because it is not a portrait - it is the lens: it decides what every
      // card costs (the loyalty wheel) and which quarter of the collection is closed to you. Naming
      // a deck before choosing its hero is choosing the label before the thing.
      const newBtn = document.createElement("button");
      newBtn.type = "button";
      newBtn.className = "deck__tab deck__tab--new";
      newBtn.dataset["label"] = "NEW DECK";
      newBtn.textContent = "+ NEW DECK";
      // ⚠️ **THE FOUR FREE DECKS DO NOT COUNT AGAINST THE LIMIT.** They cannot be deleted, so
      // counting them would silently cut a 6-deck allowance to 2 with no way to free a slot.
      const builtCount = decks.filter((d) => d.isStarter !== true).length;
      // ⭐ **SLOTS, NOT A FLAT CAP.** One free build slot; more are bought. The four pre-built decks
      // never count against it - they cannot be deleted, so counting them would leave no way to
      // free a slot.
      const slots = Math.min(decks[0]?.deckSlots ?? 1, limits.maxDeckCount);
      newBtn.disabled = builtCount >= slots;
      newBtn.title = newBtn.disabled
        ? (builtCount >= limits.maxDeckCount
            ? `You have the maximum of ${limits.maxDeckCount} decks.`
            : `You are using your ${slots} deck slot${slots === 1 ? "" : "s"}. `
              + "Unlock another to build more, or delete a deck.")
        : `Choose a hero, then build (${builtCount} of ${slots} slot`
          + `${slots === 1 ? "" : "s"} used)`;
      newBtn.textContent = newBtn.disabled && builtCount < limits.maxDeckCount
        ? `+ NEW DECK (${builtCount}/${slots})`
        : "+ NEW DECK";
      newBtn.addEventListener("click", () => {
        audio.play("click", 0.7);
        openHeroPicker();
      });
      picker.append(newBtn);

      // ⭐ **A DECK IS NAMEABLE AFTER IT IS MADE, NOT ONLY WHEN IT IS MADE.** Alex, 2026-08-14:
      // *"can a player name their deck? they should be able to."* They could at creation; renaming
      // is how a deck actually gets its name, because you rarely know what it is until you have
      // built it.
      //
      // ⚠️ Click-to-edit rather than a permanent input: this is a heading most of the time, and a
      // text box sitting in a heading slot invites a click nobody meant to make.
      const heroLine = document.createElement("div");
      heroLine.className = "deck__hero";

      const nameBtn = document.createElement("button");
      nameBtn.type = "button";
      nameBtn.className = "deck__name";
      nameBtn.dataset["label"] = "RENAME";
      nameBtn.textContent = String(deck.name ?? "Untitled deck");
      nameBtn.title = deck.isStarter ? "Starter decks keep their name" : "Click to rename";
      if (deck.isStarter) {
        nameBtn.classList.add("deck__name--fixed");
        nameBtn.disabled = true;
      }
      nameBtn.addEventListener("click", () => {
        const input = document.createElement("input");
        input.className = "duel__codein deck__nameedit";
        input.value = String(deck.name ?? "");
        input.maxLength = 40;
        input.setAttribute("aria-label", "Deck name");

        const commit = async (): Promise<void> => {
          const next = input.value.trim();
          input.replaceWith(nameBtn);
          if (!next || next === deck.name) return;
          const previous = deck.name;
          deck.name = next;
          nameBtn.textContent = next;
          draw();
          try {
            await Api.saveDeck({ ...(deck.id ? { id: deck.id } : {}), name: next,
                                 ownedCardIds: [...chosen] });
          } catch (e) {
            // ⚠️ Put the old name BACK on a failed save. A rename that looks applied and is not is
            // how a player loses track of which deck is which.
            deck.name = previous ?? "";
            nameBtn.textContent = String(previous ?? "");
            draw();
            const note_ = note(`Could not rename: ${describe(e)}`, true);
            foot.prepend(note_);
          }
        };
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") input.replaceWith(nameBtn);
        });
        input.addEventListener("blur", () => void commit());
        nameBtn.replaceWith(input);
        input.focus();
        input.select();
      });
      heroLine.append(nameBtn);
      const heroName = document.createElement("span");
      heroName.className = "deck__heroname";
      heroName.textContent = cardName(deck.heroId) || "No hero";
      const heroTag = document.createElement("span");
      heroTag.className = "deck__herorealm";
      heroTag.textContent = hr ? hr.replace(/_/g, " ") : "";
      if (hr && REALM_COLOUR[hr]) heroTag.style.color = REALM_COLOUR[hr]!;
      heroLine.append(heroName, heroTag);

      // ⭐ The count rail: one glance, always the same place. `total/max`, with the minimum marked,
      // because "30 is legal" is the fact a builder needs and a bare count does not carry it.
      const rail = document.createElement("div");
      rail.className = "deck__rail";
      const fill = document.createElement("div");
      fill.className = "deck__railfill";
      fill.style.width = `${Math.min(100, (total / limits.maxCardsInDeck) * 100)}%`;
      const mark = document.createElement("div");
      mark.className = "deck__railmin";
      mark.style.left = `${(limits.minCardsInDeck / limits.maxCardsInDeck) * 100}%`;
      mark.title = `minimum ${limits.minCardsInDeck}`;
      const count = document.createElement("span");
      count.className = "deck__count";
      count.textContent = `${total}/${limits.maxCardsInDeck}`;
      count.classList.toggle("deck__count--ok", total >= limits.minCardsInDeck
        && total <= limits.maxCardsInDeck);
      rail.append(fill, mark);

      /*
       * ⭐ THE VIEW CONTROLS — SORTS AND FILTERS, VISIBLY DIFFERENT.
       *
       * 🔴 THEY USED TO BE ONE ROW OF IDENTICAL CHIPS, and that one defect produced three separate
       * questions from Alex in a minute: *"what is COST"*, *"what does NAMED mean"*, and *"the
       * OWNED filter doesnt work"* (it did — the greyed cards were NEMESIS, not unowned). A chip
       * that reorders and a chip that hides look the same, so the row taught nobody what it was.
       * Now they are two labelled groups: SORT ... and SHOW ...
       */
      /**
       * ⚠️ `display: contents` on a wide screen, so this wrapper changes NOTHING about the row the
       * bar has always been. It exists only so the whole set can be moved into the sheet in one
       * call on a short screen.
       */
      const groups = document.createElement("div");
      groups.className = "deck__viewgroups";

      const sortLabel = document.createElement("span");
      sortLabel.className = "deck__viewlabel";
      sortLabel.textContent = "SORT";
      groups.append(sortLabel);

      const sorts: Array<[SortKey, string]> = [
        ["cost", "COST"], ["name", "NAME"], ["realm", "REALM"],
      ];
      for (const [key, label] of sorts) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "deck__sort" + (sortBy === key ? " deck__sort--on" : "");
        b.dataset["label"] = label;
        b.textContent = label;
        b.addEventListener("click", () => { sortBy = key; audio.play("click", 0.5); draw(); });
        groups.append(b);
      }
      const ownedBtn = document.createElement("button");
      ownedBtn.type = "button";
      const showLabel = document.createElement("span");
      showLabel.className = "deck__viewlabel deck__viewlabel--show";
      showLabel.textContent = "SHOW ONLY";
      groups.append(showLabel);

      ownedBtn.className = "deck__filter" + (ownedOnly ? " deck__filter--on" : "");
      ownedBtn.dataset["label"] = "OWNED";
      ownedBtn.textContent = "OWNED";
      ownedBtn.title = "Show only the cards in your collection";
      ownedBtn.addEventListener("click", () => { ownedOnly = !ownedOnly; audio.play("click", 0.5); draw(); });
      groups.append(ownedBtn);

      // ⭐ **WINNING A CARD HAD NO VISIBLE EFFECT ANYWHERE, AND A PLAYER READ THAT AS NOTHING
      // HAPPENING.** Alex, 2026-08-15, after winning Woodland Wisp: *"I won this card, the deck
      // still says 30/35. Shouldn't it now say 31/35?"*
      //
      // ⚠️ **30/35 IS THE DECK, AND IT WAS RIGHT.** A won card joins your COLLECTION, not a deck -
      // and that deck is a read-only starter that already contained the card. Nothing was broken.
      // But the only number on the screen answered a question he was not asking, and the number that
      // WOULD have answered his - how much of the set do I own - was displayed nowhere at all.
      //
      // ⭐ So the fix is not to change the deck count. It is to SHOW THE COLLECTION, beside the
      // control that filters by it, where a player looking for "did my win count" will find it.
      const ownedCount = document.createElement("span");
      // ⚠️ NOT `deck__collection` - that class is already the left PANE (line ~207), and reusing it
      // would have given this span the pane's layout. Caught before it shipped; a class name is an
      // identifier and colliding on one is the same defect as colliding on a variable.
      ownedCount.className = "deck__owncount";
      const distinct = new Set(owned.map((o) => String(o.cardId))).size;
      ownedCount.textContent = `${distinct} / ${allCards().length} collected`;
      // ⚠️ DISTINCT CARDS, not owned entities. Two copies of one card are two entities (see the note
      // at the top of this file), so counting entities would show a collection larger than the game.
      ownedCount.title = "Distinct cards in your collection. Win more from campaign stages.";
      groups.append(ownedCount);

      // ⭐ **ONE "WINNABLE" CONTROL, NOT SEVERAL.** Alex, 2026-08-15: *"it should also sort by the
      // ones that you can win and where you can win them. So it is not messy and too many sort
      // features - there can be one sort for all of those winnables."*
      //
      // 🔴 **IT IS CALLED "NAMED", AND IT USED TO BE CALLED "WINNABLE", WHICH WAS FALSE.**
      // Alex: *"it still shows just 5 winnable NFTs."* All 171 ARE winnable — `CardPool()` takes
      // every card id unfiltered and ~88 of a playthrough's 93 grants are random draws from it.
      // Five is how many a stage NAMES. The old label turned a useful filter into a claim that the
      // other 166 were locked away, which is the kind of lie a tooltip cannot undo — so the LABEL
      // changed rather than the tooltip getting longer.
      const winBtn = document.createElement("button");
      winBtn.type = "button";
      winBtn.className = "deck__filter" + (namedOnly ? " deck__filter--on" : "");
      // ⛔ NOT "WINNABLE" (every card is) and NOT "NAMED" (Alex: *"what does NAMED mean?"* — a label
      // that needs its own explanation is not a label). GUARANTEED says the useful thing: these are
      // the cards you can go and win ON PURPOSE, rather than hope for from a random draw.
      winBtn.dataset["label"] = "GUARANTEED";
      winBtn.textContent = "GUARANTEED";
      // ⛔ NOT "WINNABLE". Every card in the set is winnable; only five are NAMED by a stage.
      winBtn.title =
        `The ${Object.keys(CARD_SOURCES).length} cards a campaign stage awards BY NAME, so you can `
        + `go and win them on purpose. Every other card is winnable too, just not guaranteed — `
        + RANDOM_CARD_SOURCE;
      winBtn.addEventListener("click", () => {
        namedOnly = !namedOnly; audio.play("click", 0.5); draw();
      });
      groups.append(winBtn);

      const anyNft = [...byCard.keys()].some(hasNft);
      const nftBtn = document.createElement("button");
      nftBtn.type = "button";
      nftBtn.className = "deck__filter deck__filter--nft" + (nftOnly ? " deck__filter--on" : "");
      nftBtn.dataset["label"] = "NFT";
      nftBtn.textContent = "NFT";
      // ⚠️ Shown even with none held, and SAID SO. Hiding it would make a player who just minted
      // one wonder where the filter went; the tooltip is the honest version.
      nftBtn.title = anyNft ? "Show only cards you hold as NFTs" : "You do not hold any NFT cards yet";
      nftBtn.disabled = !anyNft;
      nftBtn.addEventListener("click", () => { nftOnly = !nftOnly; audio.play("click", 0.5); draw(); });
      groups.append(nftBtn);

      /**
       * ⭐ **FIND, rather than SCROLL.** The three filters cut the set by a property and the sort
       * reorders it; neither answers "where is Ambush Wolf". Placed last in the bar so the two
       * labelled groups a player already learned (SORT ..., SHOW ONLY ...) keep their reading
       * order, and it is the one control that is useful with no groups understood at all.
       *
       * ⚠️ `duel__codein` is the project's existing text-field skin (the deck name editor uses it
       * too). A second field style would be a second owner of what an input looks like.
       */
      const find = document.createElement("input");
      find.type = "search";
      find.className = "duel__codein deck__find";
      find.placeholder = "FIND A CARD";
      find.setAttribute("aria-label", "Find a card by name");
      find.value = search;
      find.addEventListener("input", () => {
        search = find.value.trim().toLowerCase();
        applySearch();
      });
      /**
       * The way to the sort and filter groups on a screen that cannot show them in a row.
       *
       * ⚠️ **SHOWN BY CSS, ON THE SAME `max-height` THE ROW LAYOUT KEYS ON**, never by a width test
       * in TypeScript. Two owners of "is this a phone" is how a layout and its controls end up
       * disagreeing, and `mountStage` already owns that question.
       */
      const moreBtn = document.createElement("button");
      moreBtn.type = "button";
      moreBtn.className = "deck__sort deck__viewmore";
      moreBtn.dataset["label"] = "VIEW";
      const activeFilters = [ownedOnly, namedOnly, nftOnly].filter(Boolean).length;
      // ⭐ The count is on the button because the sheet is CLOSED most of the time: a filter that is
      // on, out of sight, is exactly how "the OWNED filter doesnt work" gets reported again.
      moreBtn.textContent = activeFilters > 0 ? `VIEW (${activeFilters})` : "VIEW";
      moreBtn.title = "Sort and filter the collection";
      moreBtn.addEventListener("click", () => {
        sheetOpen = true; audio.play("click", 0.5); draw();
      });

      view.replaceChildren(groups, find, moreBtn);

      if (sheetOpen) {
        const card = document.createElement("div");
        card.className = "settings__card";
        const h = document.createElement("h2");
        h.className = "settings__title";
        h.textContent = "SORT AND FILTER";
        const done = document.createElement("button");
        done.type = "button";
        done.className = "plate plate--quiet deck__viewdone";
        done.dataset["label"] = "DONE";
        // ⚠️ A plate is a FACE plus a LABEL, not a button with text on it. Setting `textContent`
        // alone produced a plate-sized string with no plate under it: the class was there and the
        // shape was not.
        const doneFace = document.createElement("span");
        doneFace.className = "plate__face";
        doneFace.setAttribute("aria-hidden", "true");
        const doneLabel = document.createElement("span");
        doneLabel.className = "plate__label";
        doneLabel.textContent = "DONE";
        done.append(doneFace, doneLabel);
        done.addEventListener("click", () => { sheetOpen = false; audio.play("click", 0.5); draw(); });
        // ⚠️ `append` MOVES `groups` out of the bar. One set of controls, in one place at a time.
        card.append(h, groups, done);
        viewSheet.replaceChildren(card);
      }
      viewSheet.hidden = !sheetOpen;

      head.append(picker, heroLine, rail, count);

      // ── the collection ───────────────────────────────────────────────────────────────────────
      collection.replaceChildren();
      const everyCard = allCards()
        .map((c) => String(c.id ?? ""))
        .filter((id) => id !== "");
      const cards = everyCard
        .filter((id) => !nftOnly || hasNft(id))
        .filter((id) => !namedOnly || (CARD_SOURCES[id]?.length ?? 0) > 0)
        .filter((id) => !ownedOnly || byCard.has(id))
        .sort((a, b) => {
          if (sortBy === "name") return cardName(a).localeCompare(cardName(b));
          if (sortBy === "realm") {
            // ⚠️ Grouped by realm, then by cost inside it - a realm block ordered randomly is not
            // easier to read than no grouping at all.
            const ra = cardRealm(a) ?? "", rb = cardRealm(b) ?? "";
            if (ra !== rb) return ra.localeCompare(rb);
          }
          const ca = effectiveCost(num(cardRecord(a)?.mana, 0), hr, cardRealm(a));
          const cb = effectiveCost(num(cardRecord(b)?.mana, 0), hr, cardRealm(b));
          return ca - cb || cardName(a).localeCompare(cardName(b));
        });

      for (const cardId of cards) {
        const rec = cardRecord(cardId);
        const realm = cardRealm(cardId);
        const loyalty = loyaltyOf(hr, realm);
        const allowed = canInclude(hr, realm);
        const base = num(rec?.mana, 0);
        const cost = effectiveCost(base, hr, realm);
        const held = (byCard.get(cardId) ?? []).length;
        const ownIt = held > 0;
        const inDeck = copiesOf(cardId);
        const limit = num(rec?.limitInDeck, 1);
        const full = ownIt && inDeck >= Math.min(limit, held);

        // ⭐ **A REAL CARD, NOT A TEXT ROW.** Alex, 2026-08-14: *"when the player is building their
        // deck they must be able to see the beautiful images on the cards, not just text."* He is
        // right, and the fix is not to bolt an <img> onto a tile of my own - it is to render the
        // SHIPPED card. `cardEl` already owns the art, the realm frame, the mask, the shine and the
        // badges, and it is what the player sees on the board. A second way of drawing a card is a
        // second owner, and it is how the builder and the board drift apart.
        //
        // ⭐ **AND THE COST IT SHOWS IS THE EFFECTIVE ONE.** The card is drawn with the cost it will
        // actually have in THIS deck, so the loyalty rule is expressed in the card's own cost badge
        // rather than in a footnote. Change the hero and every card visibly re-prices itself.
        const view: RuntimeObject = {
          DataId: cardId,
          Mana: { Current: cost },
          Attack: { Current: num(rec?.attack, 0) },
          Hp: { Current: num(rec?.hp, 0) },
        };

        const holder = document.createElement("div");
        holder.className = "deck__slot";
        holder.dataset["loyalty"] = loyalty;
        if (realm && REALM_COLOUR[realm]) holder.style.setProperty("--realm", REALM_COLOUR[realm]!);

        const card = cardEl(view, {
          mini: true,
          // ⭐ **OUTSIDE A MATCH THE COLLECTION IS THE OWNER OF THIS QUESTION.** In a match the
          // SERVER decides (`MatchSession.MarkFoils`, after the per-viewer mask) because only it can
          // speak for both seats. Here there is no opponent and no wire: `hasNft` reads the owned
          // entities this screen already holds, which is a better answer than any the match has.
          foil: hasNft(cardId),
          selectable: ownIt && allowed && !full && !readOnly,
          ...(ownIt && allowed && !full && !readOnly ? { onToggle: () => addCopy(cardId) } : {}),
        });
        card.classList.add("deck__card");
        holder.append(card);

        // ⚠️ The overlays are SIBLINGS of the card, never children: `cardEl` owns the card's
        // internals, and reaching into them from here would break the moment it changes.
        if (cost !== base) {
          const up = document.createElement("span");
          up.className = "deck__penalty";
          up.textContent = `+${cost - base}`;
          up.title = `${loyalty}: costs ${cost - base} more with this hero`;
          holder.append(up);
        }

        /*
         * ⭐ "WON" — and it is a RECEIPT, so it is allowed to be past tense. Per the sweep rule: a
         * past-tense claim is only permitted where the code has proof for THAT COPY, and an award
         * ledger entry is exactly that proof. This is the one badge that tells a player which of
         * their cards can actually be minted.
         *
         * ⚠️ Not shown when the card is already an NFT — "WON" and "NFT" in the same corner would
         * be two badges saying overlapping things, and the NFT is the stronger statement.
         */
        if (wonIds.has(cardId) && !hasNft(cardId)) {
          const w = document.createElement("span");
          w.className = "deck__wonmark";
          w.textContent = "WON";
          w.title = "You won this card in a campaign. It is yours to mint at the Revival Tent.";
          holder.append(w);
        }

        if (hasNft(cardId)) {
          // ⭐ A minted card is the player's PROPERTY, not just a grant - it deserves to be visibly
          // different from a card the game handed out.
          const nft = document.createElement("span");
          nft.className = "deck__nft";
          nft.textContent = "NFT";
          nft.title = "You hold this card as an NFT";
          holder.append(nft);
          holder.classList.add("deck__slot--nft");
        }

        const badge = document.createElement("span");
        badge.className = "deck__owned";
        badge.textContent = !ownIt
          ? "0"
          : inDeck > 0 ? `${inDeck}/${Math.min(limit, held)}` : `x${held}`;
        if (inDeck > 0) badge.classList.add("deck__owned--in");
        holder.append(badge);

        if (!ownIt) {
          // ⚠️ Marked as NOT OWNED rather than hidden or shown as broken. It is a real card in the
          // game that this player does not have - which is a fact, not an error.
          holder.classList.add("deck__slot--unowned");
          // ⚠️ **THE WORDING MATTERS AND THE FIRST ONE MISLED.** "NOT OWNED" beside an NFT
          // filter reads as "an NFT you do not own" - Alex, 2026-08-14: *"we have not minted any
          // NFTs so how are you saying there are 90+ unowned NFTs?"* There are none, and these have
          // nothing to do with minting: they are ordinary cards in the database that this account
          // has never been granted. "NOT IN YOUR COLLECTION" cannot be read the other way.
          const lock = document.createElement("span");
          lock.className = "deck__lock deck__lock--unowned";
          lock.textContent = "NOT YOURS YET";
          holder.append(lock);
        } else if (!allowed) {
          holder.classList.add("deck__slot--forbidden");
          const lock = document.createElement("span");
          // ⚠️ A DIFFERENT CLASS, BECAUSE IT IS A DIFFERENT FACT. "NEMESIS" (you own it, this hero
          // cannot play it) rendered identically to "NOT YOURS YET" (you do not own it) — same
          // grey, same overlay — so Alex read a screen full of owned nemesis cards as the OWNED
          // filter being broken. Two meanings sharing one look is the bug, not the filter.
          lock.className = "deck__lock deck__lock--nemesis";
          lock.textContent = "NEMESIS";
          holder.append(lock);
        } else if (full) {
          holder.classList.add("deck__slot--full");
        }

        holder.title = !ownIt
          // ⚠️ **SAY HOW IT IS OBTAINED, NOT JUST THAT IT IS NOT YOURS.** "Not in your collection"
          // with no route to it is a locked door with no handle. The two real paths today are the
          // account grant and campaign/gauntlet milestone rewards; when cards are minted, the
          // marketplace becomes the third and this line is where it goes.
          ? "Not in your collection yet. Cards are granted when you start and by campaign "
            + "milestones."
          : allowed
          ? `${loyalty}${cost !== base ? ` — costs ${cost - base} more with this hero` : ""}`
          : "Your hero's nemesis realm. This card cannot go in this deck.";
        // ⚠️ **THE NAME THE PLAYER READS, LOWERCASED ONCE HERE.** `cardName()` goes through the
        // display-name owner, so searching this attribute searches exactly what is on the tile -
        // never the engine id, which no player has ever seen.
        holder.dataset["find"] = cardName(cardId).toLowerCase();
        collection.append(holder);
      }

      applySearch();

      // ── the deck side: curve, then the list ──────────────────────────────────────────────────
      side.replaceChildren();

      const curveTitle = document.createElement("h2");
      curveTitle.className = "deck__sidehead";
      curveTitle.textContent = "CURVE";
      const curve = document.createElement("div");
      curve.className = "deck__curve";
      const buckets = new Map<number, number>();
      for (const e of entries) {
        const c = effectiveCost(num(cardRecord(e.cardId)?.mana, 0), hr, e.realm);
        const key = Math.min(c, 8);
        buckets.set(key, (buckets.get(key) ?? 0) + e.copies);
      }
      const peak = Math.max(1, ...buckets.values());
      for (let c = 0; c <= 8; c++) {
        const n = buckets.get(c) ?? 0;
        const bar = document.createElement("div");
        bar.className = "deck__bar";
        bar.dataset["n"] = String(n);
        // ⚠️ **A REAL CHILD, NOT A `::before`.** The pseudo-element version rendered nothing: as a
        // flex item its percentage height had no definite basis to resolve against, so every bar
        // collapsed while the axis labels stayed - a histogram that looked like an empty axis. A
        // child element also means the harness can count the bars.
        const fillBar = document.createElement("div");
        fillBar.className = "deck__barfill";
        fillBar.style.height = `${(n / peak) * 100}%`;
        bar.append(fillBar);
        const lab = document.createElement("span");
        lab.className = "deck__barlab";
        lab.textContent = c === 8 ? "8+" : String(c);
        bar.append(lab);
        curve.append(bar);
      }

      const listTitle = document.createElement("h2");
      listTitle.className = "deck__sidehead";
      listTitle.textContent = "IN THIS DECK";
      const list = document.createElement("div");
      list.className = "deck__list";
      if (!entries.length) {
        list.append(note("Nothing yet. Tap a card to add it."));
      }
      for (const e of [...entries].sort((a, b) =>
        effectiveCost(num(cardRecord(a.cardId)?.mana, 0), hr, a.realm)
        - effectiveCost(num(cardRecord(b.cardId)?.mana, 0), hr, b.realm))) {
        const row = document.createElement("button");
        row.type = "button";
        row.className = "deck__row";
        row.dataset["label"] = cardName(e.cardId);
        const c = effectiveCost(num(cardRecord(e.cardId)?.mana, 0), hr, e.realm);
        const cst = document.createElement("span");
        cst.className = "deck__rowcost";
        cst.textContent = String(c);
        if (e.realm && REALM_COLOUR[e.realm]) cst.style.color = REALM_COLOUR[e.realm]!;
        const nm = document.createElement("span");
        nm.className = "deck__rowname";
        nm.textContent = cardName(e.cardId);
        const n = document.createElement("span");
        n.className = "deck__rowcount";
        n.textContent = `x${e.copies}`;
        row.append(cst, nm, n);
        row.title = readOnly ? "Starter decks cannot be changed" : "Remove one";
        if (!readOnly) row.addEventListener("click", () => removeCopy(e.cardId));
        list.append(row);
      }

      side.append(curveTitle, curve, listTitle, list);

      // ── the foot: every problem at once, then SAVE ───────────────────────────────────────────
      foot.replaceChildren();
      const issues = document.createElement("div");
      issues.className = "deck__issues";
      if (problems.length) {
        for (const p of problems) issues.append(note(p.message, p.blocking));
      } else {
        issues.append(note("This deck is legal."));
      }

      // ⭐ **DELETE, BEHIND A CONFIRM.** Alex, 2026-08-14: *"the user needs to be able to edit and
      // delete a deck that they create."* Editing was already there (cards and the name); deleting
      // was not. It is irreversible and it sits beside SAVE, so it arms first and says so - the
      // same two-step the concede control uses, for the same reason.
      //
      // ⚠️ The server refuses to remove your LAST deck. That refusal is SHOWN rather than
      // pre-empted by hiding the button: a control that vanishes teaches nothing.
      let armedDelete = false;
      let armTimer: number | null = null;
      const del = document.createElement("button");
      del.type = "button";
      del.className = "utilbtn deck__delete";
      del.dataset["label"] = "DELETE";
      del.textContent = "DELETE DECK";
      del.addEventListener("click", async () => {
        if (!armedDelete) {
          armedDelete = true;
          del.textContent = "DELETE FOREVER?";
          del.classList.add("deck__delete--armed");
          armTimer = window.setTimeout(() => {
            armedDelete = false;
            del.textContent = "DELETE DECK";
            del.classList.remove("deck__delete--armed");
          }, 4000);
          return;
        }
        if (armTimer !== null) window.clearTimeout(armTimer);
        del.disabled = true;
        try {
          await Api.deleteDeck(String(deck.id ?? ""));
          const at = decks.indexOf(deck);
          if (at >= 0) decks.splice(at, 1);
          deck = decks[0]!;
          chosen = new Set(deck.ownedCardIds ?? []);
          audio.play("cardBurn", 0.8);
          draw();
        } catch (e) {
          issues.replaceChildren(note(describe(e), true));
          armedDelete = false;
          del.disabled = false;
          del.textContent = "DELETE DECK";
          del.classList.remove("deck__delete--armed");
        }
      });

      const save = document.createElement("button");
      save.type = "button";
      save.className = "plate deck__save";
      save.dataset["label"] = "SAVE";
      const f2 = document.createElement("span");
      f2.className = "plate__face";
      f2.setAttribute("aria-hidden", "true");
      const l2 = document.createElement("span");
      l2.className = "plate__label";
      l2.textContent = "SAVE";
      save.append(f2, l2);
      const blocked = problems.some((p) => p.blocking);
      save.disabled = blocked;
      save.classList.toggle("plate--disabled", blocked);

      save.addEventListener("click", async () => {
        save.disabled = true;
        try {
          await Api.saveDeck({
            ...(deck.id ? { id: deck.id } : {}),
            ownedCardIds: [...chosen],
          });
          deck.ownedCardIds = [...chosen];
          audio.play("click");
          issues.replaceChildren(note("Saved."));
        } catch (e) {
          issues.replaceChildren(note(`Could not save: ${describe(e)}`, true));
          save.disabled = false;
        }
      });

      if (readOnly) {
        // ⭐ **AND THE WAY FORWARD IS OFFERED, NOT JUST THE REFUSAL.** "You cannot edit this" is a
        // wall; "copy it and build on it" is what the player actually wanted.
        issues.replaceChildren(
          note("This is one of your four starter decks. Copy it to build your own."));
        const copy = document.createElement("button");
        copy.type = "button";
        copy.className = "plate";
        copy.dataset["label"] = "COPY TO A NEW DECK";
        const cf2 = document.createElement("span");
        cf2.className = "plate__face";
        cf2.setAttribute("aria-hidden", "true");
        const cl2 = document.createElement("span");
        cl2.className = "plate__label";
        cl2.textContent = "COPY TO A NEW DECK";
        copy.append(cf2, cl2);
        copy.addEventListener("click", async () => {
          copy.disabled = true;
          try {
            const made = await Api.createDeck({
              name: `${deck.name ?? "Deck"} (copy)`,
              heroId: String(deck.heroId ?? ""),
              ownedCardIds: [...chosen],
            }) as DeckDto;
            decks.push(made);
            deck = made;
            chosen = new Set(made.ownedCardIds ?? []);
            audio.play("click");
            draw();
          } catch (e) {
            issues.replaceChildren(note(describe(e), true));
            copy.disabled = false;
          }
        });
        foot.append(issues, copy);
      } else {
        foot.append(issues, del, save);
      }
    }

    draw();
  },
};

function num(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
